/**
 * Prices: what each product's customers pay, changed here rather than by a
 * deploy (subscription engine, section 16).
 *
 * Prices are versions. A version is drafted, previewed against the one in
 * force, then published to start now or on a date. A published version is
 * never edited; a change is a new version. Bills raised after it starts,
 * and the public pricing page within five minutes, use it.
 */
import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import { Badge, Button, ConfirmDialog, EmptyState, Modal, PageHeader } from '@lipaflow/ui'
import { apiErrorMessage } from '@lipaflow/shared'
import { bandLabel, estimate, withEnds, type PriceList } from '@lipaflow/shared/pricing'
import { priceBookService, type NewPriceBook, type PriceBook, type PriceBookList } from '../services/priceBooks'
import { usePageTitle } from '../lib/pageTitle'
import { useSector } from '../sector/sector'

const field = 'w-full rounded-button border border-hairline bg-surface p-2.5 text-body-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent'
const money = (n: number, currency = 'KES') => `${currency} ${Math.round(n).toLocaleString('en-KE')}`
const day = (iso: string) => new Date(iso).toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' })

/** Counts worth comparing: either side of each band edge, and a few round sizes. */
function exampleCounts(books: PriceBook[]): number[] {
  const counts = new Set<number>([10, 40, 150, 320])
  for (const book of books) {
    for (const band of book.bands) {
      if (band.from_qty > 1) counts.add(band.from_qty - 1).add(band.from_qty)
    }
    const last = book.bands[book.bands.length - 1]
    if (last) counts.add(last.from_qty + 100)
  }
  return [...counts].sort((a, b) => a - b).slice(0, 12)
}

function asList(book: Pick<PriceBook, 'bands' | 'min_charge' | 'cadence_discounts' | 'band_pricing' | 'currency'>, unit: string, period: string): PriceList {
  return { unit, period, currency: book.currency, band_pricing: book.band_pricing, bands: book.bands,
    min_charge: book.min_charge, cadence_discounts: book.cadence_discounts }
}

/** The first count where one more unit lowers the bill, if any. */
function firstCliff(list: PriceList): number | null {
  const top = Math.max(...list.bands.map((b) => b.from_qty)) + 50
  let previous = 0
  for (let count = 1; count <= top; count += 1) {
    const bill = estimate(list, count).perPeriod
    if (bill === null) return null
    if (bill < previous) return count
    previous = bill
  }
  return null
}

function statusOf(book: PriceBook, inForce: string | null): { label: string; variant: 'success' | 'teal' | 'neutral' | 'warning' } {
  if (book.id === inForce) return { label: 'In force', variant: 'success' }
  if (book.status === 'draft') return { label: 'Draft', variant: 'warning' }
  if (book.status === 'archived') return { label: 'Withdrawn', variant: 'neutral' }
  return new Date(book.effective_from) > new Date() ? { label: 'Scheduled', variant: 'teal' } : { label: 'Replaced', variant: 'neutral' }
}

function BandsTable({ book, unit, period }: { book: PriceBook; unit: string; period: string }) {
  const graduated = book.band_pricing === 'graduated'
  return (
    <table className="w-full text-left text-body-sm">
      <caption className="sr-only">Price per {unit} per {period}</caption>
      <thead>
        <tr className="border-b border-hairline text-ink-muted">
          <th scope="col" className="py-2 font-medium capitalize">{unit}s</th>
          <th scope="col" className="py-2 text-right font-medium">Per {unit}, per {period}</th>
        </tr>
      </thead>
      <tbody>
        {book.bands.map((band) => (
          <tr key={band.from_qty} className="border-b border-hairline last:border-0">
            <td className="py-2 text-ink">{bandLabel(band.from_qty, band.to_qty, graduated)}</td>
            <td className="tabular py-2 text-right text-ink">{band.rate === null ? 'Agreed per customer' : money(band.rate, book.currency)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

type DraftBand = { from_qty: string; rate: string }

interface EditorProps {
  verticalId: string
  productCode: string
  productName: string
  unit: string
  period: string
  current: PriceBook | null
  onClose: () => void
}

function PriceEditor({ verticalId, productCode, productName, unit, period, current, onClose }: EditorProps) {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [bands, setBands] = useState<DraftBand[]>(
    current?.bands.map((b) => ({ from_qty: String(b.from_qty), rate: b.rate === null ? '' : String(b.rate) })) ?? [{ from_qty: '1', rate: '' }],
  )
  const [minCharge, setMinCharge] = useState(String(current?.min_charge ?? 0))
  const [yearlyPct, setYearlyPct] = useState(String(Math.round((current?.cadence_discounts.yearly ?? 0.15) * 100)))
  const [bandPricing, setBandPricing] = useState<'graduated' | 'volume'>('graduated')
  const [when, setWhen] = useState<'now' | 'date'>('now')
  const [date, setDate] = useState('')
  const [tryCount, setTryCount] = useState('')
  const [confirming, setConfirming] = useState(false)

  const parsed = bands.map((b) => ({ from_qty: Number(b.from_qty), rate: b.rate.trim() === '' ? null : Number(b.rate) }))
  const problems: string[] = []
  if (!name.trim()) problems.push('Give this version a name, e.g. "Term 1 2027 prices".')
  if (parsed[0]?.from_qty !== 1) problems.push('The first band must start at 1.')
  parsed.forEach((b, i) => {
    if (!Number.isInteger(b.from_qty) || b.from_qty < 1) problems.push(`Band ${i + 1}: where it starts must be a whole number.`)
    if (i > 0 && b.from_qty <= parsed[i - 1].from_qty) problems.push(`Band ${i + 1} must start after band ${i}.`)
    if (b.rate !== null && (Number.isNaN(b.rate) || b.rate < 0)) problems.push(`Band ${i + 1}: the price can't be negative.`)
    if (b.rate === null && i !== parsed.length - 1) problems.push(`Band ${i + 1} needs a price; only the last band may be agreed per customer.`)
  })
  const minimum = Number(minCharge)
  const pct = Number(yearlyPct)
  if (Number.isNaN(minimum) || minimum < 0) problems.push("The minimum charge can't be negative.")
  if (Number.isNaN(pct) || pct < 0 || pct >= 100) problems.push('The yearly discount is a percentage from 0 to 99.')
  if (when === 'date' && !date) problems.push('Pick the date the prices start.')

  const proposed: PriceList | null = problems.length ? null : {
    unit, period, currency: current?.currency ?? 'KES', band_pricing: bandPricing,
    bands: withEnds(parsed), min_charge: minimum, cadence_discounts: pct > 0 ? { yearly: pct / 100 } : {},
  }
  const before = current ? asList(current, unit, period) : null
  const cliff = proposed ? firstCliff(proposed) : null
  const counts = useMemo(() => {
    const set = new Set(current ? exampleCounts([current]) : [10, 40, 150, 320])
    for (const b of parsed) if (b.from_qty > 1) set.add(b.from_qty - 1).add(b.from_qty)
    const extra = Number(tryCount)
    if (Number.isInteger(extra) && extra > 0) set.add(extra)
    return [...set].sort((a, b) => a - b).slice(0, 14)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, bands, tryCount])

  const save = useMutation({
    mutationFn: (publish: boolean) => {
      const book: NewPriceBook = {
        name: name.trim(),
        product_code: productCode,
        bands: parsed,
        min_charge: minimum,
        cadence_discounts: pct > 0 ? { yearly: pct / 100 } : {},
        band_pricing: bandPricing,
        publish,
        ...(when === 'date' ? { effective_from: `${date}T00:00:00+03:00` } : {}),
      }
      return priceBookService.create(verticalId, book)
    },
    onSuccess: (book) => {
      toast.success(book.status === 'published'
        ? when === 'now' ? 'Published. New bills use these prices now.' : `Scheduled from ${day(book.effective_from)}.`
        : 'Saved as a draft.')
      queryClient.invalidateQueries({ queryKey: ['price-books', verticalId] })
      onClose()
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Couldn't save the prices")),
  })

  const describeStart = when === 'now' ? 'now' : date ? `on ${day(`${date}T00:00:00+03:00`)}` : 'on the date you pick'

  return (
    <Modal open onClose={onClose} title={`New prices: ${productName}`} size="xl">
      <div className="space-y-6">
        <div>
          <label htmlFor="pb-name" className="mb-1 block text-body-sm font-medium text-ink">Name</label>
          <input id="pb-name" className={field} value={name} onChange={(e) => setName(e.target.value)} placeholder="Term 1 2027 prices" />
        </div>

        <fieldset>
          <legend className="mb-2 text-body-sm font-medium text-ink">Bands: price per {unit}, per {period}</legend>
          <div className="space-y-2">
            {bands.map((band, i) => (
              <div key={i} className="flex items-end gap-2">
                <div className="w-40">
                  <label htmlFor={`pb-from-${i}`} className="mb-1 block text-caption text-ink-muted">Starts at {unit}</label>
                  <input id={`pb-from-${i}`} className={field} inputMode="numeric" value={band.from_qty} disabled={i === 0}
                    onChange={(e) => setBands(bands.map((b, j) => (j === i ? { ...b, from_qty: e.target.value } : b)))} />
                </div>
                <div className="w-40">
                  <label htmlFor={`pb-rate-${i}`} className="mb-1 block text-caption text-ink-muted">Price ({current?.currency ?? 'KES'})</label>
                  <input id={`pb-rate-${i}`} className={field} inputMode="decimal" value={band.rate}
                    placeholder={i === bands.length - 1 ? 'Blank: agreed' : ''}
                    onChange={(e) => setBands(bands.map((b, j) => (j === i ? { ...b, rate: e.target.value } : b)))} />
                </div>
                {i > 0 && (
                  <Button variant="ghost" size="sm" onClick={() => setBands(bands.filter((_, j) => j !== i))} aria-label={`Remove band ${i + 1}`}>
                    Remove
                  </Button>
                )}
              </div>
            ))}
          </div>
          <Button variant="secondary" size="sm" className="mt-3" onClick={() => {
            const last = Number(bands[bands.length - 1]?.from_qty) || 1
            setBands([...bands, { from_qty: String(last + 100), rate: '' }])
          }}>
            Add a band
          </Button>
        </fieldset>

        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <label htmlFor="pb-min" className="mb-1 block text-body-sm font-medium text-ink">Minimum per {period}</label>
            <input id="pb-min" className={field} inputMode="decimal" value={minCharge} onChange={(e) => setMinCharge(e.target.value)} />
          </div>
          <div>
            <label htmlFor="pb-yearly" className="mb-1 block text-body-sm font-medium text-ink">Yearly discount (%)</label>
            <input id="pb-yearly" className={field} inputMode="decimal" value={yearlyPct} onChange={(e) => setYearlyPct(e.target.value)} />
          </div>
          <div>
            <label htmlFor="pb-pricing" className="mb-1 block text-body-sm font-medium text-ink">How bands combine</label>
            <select id="pb-pricing" className={field} value={bandPricing} onChange={(e) => setBandPricing(e.target.value as 'graduated' | 'volume')}>
              <option value="graduated">Each rate on the {unit}s in its band</option>
              <option value="volume">Whole count at one band's rate</option>
            </select>
          </div>
        </div>

        <fieldset>
          <legend className="mb-2 text-body-sm font-medium text-ink">Starts</legend>
          <div className="flex flex-wrap items-center gap-4 text-body-sm text-ink">
            <label className="flex items-center gap-2">
              <input type="radio" name="pb-when" checked={when === 'now'} onChange={() => setWhen('now')} /> Now
            </label>
            <label className="flex items-center gap-2">
              <input type="radio" name="pb-when" checked={when === 'date'} onChange={() => setWhen('date')} /> On a date
            </label>
            {when === 'date' && (
              <input type="date" aria-label="Start date" className={clsx(field, 'w-auto')} value={date}
                min={new Date().toISOString().slice(0, 10)} onChange={(e) => setDate(e.target.value)} />
            )}
          </div>
        </fieldset>

        <section aria-labelledby="pb-preview" className="rounded-card border border-hairline bg-surface-alt p-4">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <h3 id="pb-preview" className="font-outfit text-h4 text-ink">What customers would pay</h3>
            <div className="flex items-center gap-2">
              <label htmlFor="pb-try" className="text-caption text-ink-muted">Try a count</label>
              <input id="pb-try" className={clsx(field, 'w-24')} inputMode="numeric" value={tryCount} onChange={(e) => setTryCount(e.target.value)} />
            </div>
          </div>
          {!proposed ? (
            <ul className="mt-3 list-disc space-y-1 pl-5 text-body-sm text-warning">
              {problems.map((p) => <li key={p}>{p}</li>)}
            </ul>
          ) : (
            <>
              {cliff !== null && (
                <p role="alert" className="mt-3 rounded-button bg-warning-soft px-3 py-2 text-body-sm text-warning">
                  At {cliff.toLocaleString('en-KE')} {unit}s the bill is lower than at {(cliff - 1).toLocaleString('en-KE')}: customers would gain by adding {unit}s. Check the rates, or let each rate apply only to its band.
                </p>
              )}
              <table className="mt-3 w-full text-left text-body-sm">
                <thead>
                  <tr className="border-b border-hairline text-ink-muted">
                    <th scope="col" className="py-1.5 font-medium capitalize">{unit}s</th>
                    {before && <th scope="col" className="py-1.5 text-right font-medium">Now</th>}
                    <th scope="col" className="py-1.5 text-right font-medium">New, per {period}</th>
                    {before && <th scope="col" className="py-1.5 text-right font-medium">Change</th>}
                    <th scope="col" className="py-1.5 text-right font-medium">New, a year up front</th>
                  </tr>
                </thead>
                <tbody>
                  {counts.map((count) => {
                    const now = before ? estimate(before, count).perPeriod : null
                    const next = estimate(proposed, count)
                    const change = now !== null && next.perPeriod !== null ? next.perPeriod - now : null
                    return (
                      <tr key={count} className="border-b border-hairline last:border-0">
                        <td className="tabular py-1.5 text-ink">{count.toLocaleString('en-KE')}</td>
                        {before && <td className="tabular py-1.5 text-right text-ink-muted">{now === null ? 'Agreed' : money(now, proposed.currency)}</td>}
                        <td className="tabular py-1.5 text-right text-ink">{next.perPeriod === null ? 'Agreed' : money(next.perPeriod, proposed.currency)}</td>
                        {before && (
                          <td className={clsx('tabular py-1.5 text-right', change !== null && change > 0 ? 'text-warning' : 'text-ink-muted')}>
                            {change === null ? '—' : change === 0 ? 'Same' : `${change > 0 ? '+' : '−'}${money(Math.abs(change), proposed.currency)}`}
                          </td>
                        )}
                        <td className="tabular py-1.5 text-right text-ink-muted">{next.yearly === null ? 'Agreed' : money(next.yearly, proposed.currency)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </>
          )}
        </section>

        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="secondary" disabled={!proposed || save.isPending} onClick={() => save.mutate(false)}>Save as draft</Button>
          <Button disabled={!proposed || save.isPending} onClick={() => setConfirming(true)}>
            {when === 'now' ? 'Publish now' : 'Schedule'}
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={() => { setConfirming(false); save.mutate(true) }}
        title={when === 'now' ? 'Publish these prices now?' : 'Schedule these prices?'}
        confirmLabel={when === 'now' ? 'Publish now' : 'Schedule'}
        variant="primary"
        isPending={save.isPending}
      >
        <p>
          Every {productName.toLowerCase()} bill raised {describeStart} or later uses them, and the website shows them within
          five minutes. A published version can't be edited: to change it, publish a new one.
          {when === 'date' && ' You can withdraw it until it starts.'}
        </p>
      </ConfirmDialog>
    </Modal>
  )
}

/** A bundle's price is a discount off its parts: a new version changes only that. */
function BundleEditor({ verticalId, productCode, productName, current, onClose }: {
  verticalId: string; productCode: string; productName: string; current: PriceBook | null; onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [pct, setPct] = useState(String(Math.round((current?.bundle_discount ?? 0.2) * 100)))
  const [yearlyPct, setYearlyPct] = useState(String(Math.round((current?.cadence_discounts.yearly ?? 0.15) * 100)))
  const discount = Number(pct)
  const yearly = Number(yearlyPct)
  const valid = name.trim().length > 0 && discount >= 0 && discount < 100 && yearly >= 0 && yearly < 100
  const save = useMutation({
    mutationFn: (publish: boolean) => priceBookService.create(verticalId, {
      name: name.trim(), product_code: productCode, bundle_discount: discount / 100, bands: [], min_charge: 0,
      cadence_discounts: yearly > 0 ? { yearly: yearly / 100 } : {}, band_pricing: 'graduated', publish,
    }),
    onSuccess: (book) => {
      toast.success(book.status === 'published' ? 'Published. New bills use it now.' : 'Saved as a draft.')
      queryClient.invalidateQueries({ queryKey: ['price-books', verticalId] })
      onClose()
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Couldn't save the discount")),
  })
  return (
    <Modal open onClose={onClose} title={`Change ${productName}`}>
      <div className="space-y-4">
        <p className="text-body-sm text-ink-muted">
          {productName} costs its parts bought apart, at their own prices and minimums, less this discount.
        </p>
        <div>
          <label htmlFor="bundle-name" className="mb-1 block text-body-sm font-medium text-ink">Name</label>
          <input id="bundle-name" className={field} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Term 1 2027" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="bundle-pct" className="mb-1 block text-body-sm font-medium text-ink">Bundle discount (%)</label>
            <input id="bundle-pct" type="number" min={0} max={99} className={field} value={pct} onChange={(e) => setPct(e.target.value)} />
          </div>
          <div>
            <label htmlFor="bundle-yearly" className="mb-1 block text-body-sm font-medium text-ink">Yearly discount (%)</label>
            <input id="bundle-yearly" type="number" min={0} max={99} className={field} value={yearlyPct} onChange={(e) => setYearlyPct(e.target.value)} />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" disabled={!valid || save.isPending} onClick={() => save.mutate(false)}>Save as draft</Button>
          <Button disabled={!valid || save.isPending} onClick={() => save.mutate(true)}>Publish now</Button>
        </div>
      </div>
    </Modal>
  )
}

function ProductPrices({ verticalId, productName }: { verticalId: string; productName: string }) {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [startNow, setStartNow] = useState<PriceBook | null>(null)
  const [chosenProduct, setChosenProduct] = useState<string | null>(null)
  const { data, isLoading } = useQuery<PriceBookList>({
    queryKey: ['price-books', verticalId],
    queryFn: () => priceBookService.list(verticalId),
  })
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['price-books', verticalId] })
  const publish = useMutation({
    mutationFn: ({ book, now }: { book: PriceBook; now: boolean }) => priceBookService.publish(verticalId, book.id, now),
    onSuccess: (book) => {
      toast.success(new Date(book.effective_from) > new Date() ? `Scheduled from ${day(book.effective_from)}.` : 'Published. New bills use these prices now.')
      setStartNow(null)
      refresh()
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Couldn't publish")),
  })
  const archive = useMutation({
    mutationFn: (book: PriceBook) => priceBookService.archive(verticalId, book.id),
    onSuccess: () => { toast.success('Withdrawn'); refresh() },
    onError: (err) => toast.error(apiErrorMessage(err, "Couldn't withdraw")),
  })

  if (isLoading || !data) return <p role="status" className="text-body-sm text-ink-muted">Loading…</p>
  if (!data.unit || !data.period) {
    return <EmptyState title="Not sold yet" description={`${productName} has no billing set up, so it has no prices.`} />
  }
  const { unit, period } = data
  // Schools sell three products (education strategy, Phase 1), each priced
  // by its own versions; other verticals sell one.
  const productCode = chosenProduct ?? data.products[0]?.code ?? data.vertical
  const product = data.products.find((p) => p.code === productCode)
  const inForce = product ? product.in_force : data.in_force
  const books = data.price_books.filter((b) => b.product_code === productCode)
  const label = product?.name ?? productName
  const current = books.find((b) => b.id === inForce) ?? null
  const others = books.filter((b) => b.id !== inForce)

  return (
    <div className="space-y-6">
      {data.products.length > 0 && (
        <div role="radiogroup" aria-label={`${productName} products`} className="flex flex-wrap gap-2">
          {data.products.map((p) => (
            <button
              key={p.code}
              type="button"
              role="radio"
              aria-checked={p.code === productCode}
              onClick={() => setChosenProduct(p.code)}
              className={clsx(
                'rounded-button border px-3 py-1.5 text-body-sm font-medium transition-colors duration-fast',
                p.code === productCode ? 'border-accent bg-accent/10 text-accent' : 'border-hairline text-ink-muted hover:text-ink',
              )}
            >
              {p.name}
            </button>
          ))}
        </div>
      )}
      <section className="rounded-card border border-hairline bg-surface p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-outfit text-h4 text-ink">In force now</h2>
            {current ? (
              <p className="mt-1 text-body-sm text-ink-muted">
                Version {current.version}, “{current.name}”, since {day(current.effective_from)}.
              </p>
            ) : (
              <p className="mt-1 text-body-sm text-ink-muted">No prices yet: customers on {label} can't be billed.</p>
            )}
          </div>
          <Button onClick={() => setEditing(true)}>Change prices</Button>
        </div>
        {current && product && product.parts.length > 0 && (
          <p className="mt-4 text-body-sm text-ink">
            {product.parts.map((code) => data.products.find((p) => p.code === code)?.name ?? code).join(' and ')} bought
            apart, less <strong>{Math.round((current.bundle_discount ?? 0) * 100)}%</strong>
            {(current.cadence_discounts.yearly ?? 0) > 0 && `, then ${Math.round((current.cadence_discounts.yearly ?? 0) * 100)}% off yearly`}.
            Changing their prices changes this one with them.
          </p>
        )}
        {current && current.bundle_discount === null && (
          <div className="mt-4 grid gap-6 lg:grid-cols-[1.4fr_1fr]">
            <BandsTable book={current} unit={unit} period={period} />
            <dl className="space-y-2 text-body-sm">
              <div className="flex justify-between gap-4"><dt className="text-ink-muted">Minimum per {period}</dt><dd className="tabular text-ink">{money(current.min_charge, current.currency)}</dd></div>
              <div className="flex justify-between gap-4"><dt className="text-ink-muted">Yearly discount</dt><dd className="tabular text-ink">{Math.round((current.cadence_discounts.yearly ?? 0) * 100)}%</dd></div>
              <div className="flex justify-between gap-4"><dt className="text-ink-muted">Bands</dt><dd className="text-ink">{current.band_pricing === 'graduated' ? 'Each rate on its band' : 'Whole count at one rate'}</dd></div>
            </dl>
          </div>
        )}
      </section>

      {others.length > 0 && (
        <section aria-labelledby={`history-${verticalId}`}>
          <h2 id={`history-${verticalId}`} className="mb-2 font-outfit text-h4 text-ink">Other versions</h2>
          <ul className="space-y-2">
            {others.map((book) => {
              const status = statusOf(book, inForce)
              const future = new Date(book.effective_from) > new Date()
              return (
                <li key={book.id} className="rounded-card border border-hairline bg-surface p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <Badge variant={status.variant}>{status.label}</Badge>
                      <span className="text-body-sm text-ink">v{book.version} “{book.name}”</span>
                      <span className="text-caption text-ink-muted">{book.status === 'draft' ? 'starts' : 'from'} {day(book.effective_from)}</span>
                    </div>
                    <div className="flex gap-2">
                      {book.status === 'draft' && (
                        <Button size="sm" onClick={() => (future ? publish.mutate({ book, now: false }) : setStartNow(book))} disabled={publish.isPending}>
                          {future ? 'Schedule' : 'Publish'}
                        </Button>
                      )}
                      {(book.status === 'draft' || (book.status === 'published' && future)) && (
                        <Button size="sm" variant="secondary" onClick={() => archive.mutate(book)} disabled={archive.isPending}>
                          {book.status === 'draft' ? 'Discard' : 'Withdraw'}
                        </Button>
                      )}
                    </div>
                  </div>
                  <p className="mt-2 text-caption text-ink-muted">
                    {book.bundle_discount !== null
                      ? `${Math.round(book.bundle_discount * 100)}% off buying its parts apart`
                      : <>{book.bands.map((b) => `${bandLabel(b.from_qty, b.to_qty, book.band_pricing === 'graduated')}: ${b.rate === null ? 'agreed' : money(b.rate, book.currency)}`).join(' · ')}{' · '}minimum {money(book.min_charge, book.currency)}</>}
                  </p>
                </li>
              )
            })}
          </ul>
        </section>
      )}

      {editing && product && product.parts.length > 0 && (
        <BundleEditor verticalId={verticalId} productCode={productCode} productName={label} current={current}
          onClose={() => setEditing(false)} />
      )}
      {editing && !(product && product.parts.length > 0) && (
        <PriceEditor verticalId={verticalId} productCode={productCode} productName={label} unit={unit} period={period} current={current} onClose={() => setEditing(false)} />
      )}
      <ConfirmDialog
        open={startNow !== null}
        onClose={() => setStartNow(null)}
        onConfirm={() => startNow && publish.mutate({ book: startNow, now: true })}
        title="Publish this draft now?"
        confirmLabel="Publish now"
        variant="primary"
        isPending={publish.isPending}
      >
        <p>Its start date has passed, so it starts now. Every bill raised from now uses it, and the website shows it within five minutes.</p>
      </ConfirmDialog>
    </div>
  )
}

/** A sector's prices: what its customers pay, by product. */
export default function PricesPage() {
  usePageTitle('Prices')
  const sector = useSector()

  return (
    <div className="space-y-6">
      <PageHeader
        title="Prices"
        subtitle={`What ${sector.name} customers pay, by product. Changes need no deploy: new bills and the website use them once they start.`}
      />
      <ProductPrices key={sector.id} verticalId={sector.id} productName={sector.name} />
    </div>
  )
}
